import React from "react";
import {
    Box,
    Checkbox,
    Chip,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import {
    BRAND_CHECKBOX_SX,
    NEUTRAL_STATUS_COLORS,
} from "./batchAssessmentColors";

const formatLastUpdated = (isoDate) => {
    if (!isoDate) {
        return null;
    }

    return new Date(isoDate).toLocaleDateString(
        "en-US",
        {
            month: "short",
            day: "numeric",
            year: "numeric",
        }
    );
};

const BatchStudentList = ({
    students = [],
    studentState = {},
    selectedStudentId,
    onSelectStudent,
    bulkSelectedStudentIds = [],
    onToggleBulkStudent,
    totalSkills = 0,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                height: "100%",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    py: 1.75,
                    backgroundColor: "#F7F7F2",
                }}
            >
                <Typography
                    fontWeight="bold"
                    fontSize="0.95rem"
                >
                    2. Select Students
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {students.length} student
                    {students.length === 1 ? "" : "s"}
                </Typography>
            </Box>

            <Divider />

            {/* Scrollable Student List */}
            <Box
                sx={{
                    maxHeight: {
                        xs: 420,
                        md: 720,
                    },
                    overflowY: "auto",
                }}
            >
                {students.map((student) => {
                    const studentId =
                        student.student;

                    const state =
                        studentState[studentId];

                    const achievedCount = state
                        ? Object.values(
                              state.entries || {}
                          ).filter(Boolean).length
                        : 0;

                    const isSelected =
                        selectedStudentId ===
                        studentId;

                    const isBulkSelected =
                        bulkSelectedStudentIds.includes(
                            studentId
                        );

                    const lastUpdatedLabel =
                        formatLastUpdated(
                            state?.dateUpdated
                        );

                    return (
                        <Box
                            key={studentId}
                            onClick={() =>
                                onSelectStudent(
                                    studentId
                                )
                            }
                            sx={{
                                px: 1.5,
                                py: 1.25,
                                cursor: "pointer",
                                borderLeft: isSelected
                                    ? "4px solid #57643F"
                                    : "4px solid transparent",
                                backgroundColor:
                                    isSelected
                                        ? "#F5F7F1"
                                        : "transparent",
                                "&:hover": {
                                    backgroundColor:
                                        "#F7F7F7",
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                            >
                                {/* Bulk selection */}
                                <Checkbox
                                    size="small"
                                    checked={
                                        isBulkSelected
                                    }
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    onChange={() =>
                                        onToggleBulkStudent(
                                            studentId
                                        )
                                    }
                                    sx={{
                                        p: 0.5,
                                        mt: 0.25,
                                        ...BRAND_CHECKBOX_SX,
                                    }}
                                />

                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        fontWeight={
                                            isSelected
                                                ? 700
                                                : 600
                                        }
                                        fontSize="0.9rem"
                                        noWrap
                                    >
                                        {
                                            student.student_full_name
                                        }
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {achievedCount}/
                                        {totalSkills} skills demonstrated
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{
                                            mt: 0.75,
                                            flexWrap:
                                                "wrap",
                                            gap: 0.5,
                                        }}
                                    >
                                        {state?.isDirty ? (
                                            <Chip
                                                size="small"
                                                label="Unsaved changes"
                                                sx={{
                                                    height: 22,
                                                    fontSize:
                                                        "0.68rem",
                                                    fontWeight: 700,
                                                    color: NEUTRAL_STATUS_COLORS.unsaved.text,
                                                    backgroundColor: NEUTRAL_STATUS_COLORS.unsaved.bg,
                                                    border: "1px solid",
                                                    borderColor: NEUTRAL_STATUS_COLORS.unsaved.border,
                                                }}
                                            />
                                        ) : lastUpdatedLabel ? (
                                            <Chip
                                                size="small"
                                                label={
                                                    `Last update: ${lastUpdatedLabel}`
                                                }
                                                sx={{
                                                    height: 22,
                                                    fontSize:
                                                        "0.68rem",
                                                    fontWeight: 700,
                                                    color: NEUTRAL_STATUS_COLORS.not_assessed.text,
                                                    backgroundColor: NEUTRAL_STATUS_COLORS.not_assessed.bg,
                                                    border: "1px solid",
                                                    borderColor: NEUTRAL_STATUS_COLORS.not_assessed.border,
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label="Not yet saved"
                                                sx={{
                                                    height: 22,
                                                    fontSize:
                                                        "0.68rem",
                                                    fontWeight: 700,
                                                    color: NEUTRAL_STATUS_COLORS.not_assessed.text,
                                                    backgroundColor: NEUTRAL_STATUS_COLORS.not_assessed.bg,
                                                    border: "1px solid",
                                                    borderColor: NEUTRAL_STATUS_COLORS.not_assessed.border,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    );
                })}

                {students.length === 0 && (
                    <Box
                        sx={{
                            p: 3,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No students available.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default BatchStudentList;